import {useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  DeviceMobile,
  DotsThreeVertical,
  DownloadSimple,
  PlusSquare,
  ShareNetwork,
} from '@phosphor-icons/react';
import type {Icon} from '@phosphor-icons/react';
import {useTenantContext} from '@/app/TenantContext';
import {DotGlow} from '@/components/ui/DotGlow';
import {TenantFooter} from '@/components/layout/TenantFooter';
import {TenantHeader} from '@/components/layout/TenantHeader';
import {TenantLogo} from '@/components/layout/TenantLogo';
import {tenantPath} from '@/lib/tenant/resolve';
import {promptInstall, useInstall} from '@/lib/pwa/install';
import {cn} from '@/lib/utils';

type Platform = 'iphone' | 'android' | 'desktop';

interface Step {
  icon: Icon;
  title: string;
  text: string;
}

const STEPS: Record<Platform, Step[]> = {
  iphone: [
    {icon: DeviceMobile, title: 'Откройте Safari', text: 'Запустите браузер Safari и откройте ссылку на студию.'},
    {
      icon: ShareNetwork,
      title: 'Нажмите «Поделиться»',
      text: 'Кнопка «Поделиться» (квадрат со стрелкой вверх) находится внизу экрана.',
    },
    {icon: PlusSquare, title: 'На главный экран', text: 'Выберите «На экран “Домой”» и нажмите «Добавить».'},
  ],
  android: [
    {icon: DeviceMobile, title: 'Откройте Chrome', text: 'Откройте ссылку на студию в браузере Chrome.'},
    {icon: DotsThreeVertical, title: 'Откройте меню', text: 'Нажмите «⋮» (три точки) в правом верхнем углу.'},
    {icon: DownloadSimple, title: 'Установите приложение', text: 'Выберите «Установить приложение» и подтвердите.'},
  ],
  desktop: [
    {icon: DownloadSimple, title: 'Откройте сайт в Chrome или Edge', text: 'Найдите значок установки справа в адресной строке. Он появляется, когда браузер готов установить приложение.'},
    {icon: PlusSquare, title: 'Подтвердите установку', text: 'Нажмите «Установить» в окне браузера. Если значка нет, проверьте пункт установки в меню браузера.'},
    {icon: Check, title: 'Открывайте как приложение', text: 'Студия появится среди ваших приложений и будет открываться в отдельном окне.'},
  ],
};

function guessPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'iphone';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iphone';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

const TABS: {id: Platform; label: string}[] = [
  {id: 'iphone', label: 'iPhone / iPad'},
  {id: 'android', label: 'Android'},
  {id: 'desktop', label: 'Компьютер'},
];

export function InstallPage() {
  const {tenant} = useTenantContext();
  const {slug = ''} = useParams();
  const [platform, setPlatform] = useState<Platform>(() => guessPlatform());
  const install = useInstall();

  return (
    <div className="flex min-h-[calc(100dvh-6rem)] flex-1 flex-col">
      <TenantHeader />
      <div className="stagger mx-auto flex w-full max-w-[700px] flex-col gap-6 px-4 pb-8 pt-6">
        <Link to={tenantPath(slug)} className="flex items-center gap-2 text-[15px] text-muted-foreground">
          <ArrowLeft size={20} aria-hidden />
          На главную
        </Link>

        <div className="panel panel-glow flex flex-col gap-5 rounded-[32px] p-6">
          <DotGlow seed={7} />
          <div className="flex items-center gap-4">
            <TenantLogo name={tenant.name} logoUrl={tenant.logoUrl} className="h-14 w-14 shrink-0 rounded-2xl text-2xl" />
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-[17px] font-bold text-foreground">{tenant.name}</span>
              <span className="text-[15px] text-muted-foreground">Приложение студии</span>
            </div>
          </div>
          <h1 className="m-0 text-[32px] font-bold leading-[1.08] tracking-tight text-foreground">
            Добавьте студию на главный экран
          </h1>
          <p className="m-0 text-[17px] leading-relaxed text-muted-foreground">
            Своя иконка на устройстве. Быстрый доступ к услугам и вашей записи.
          </p>
          {install.installed ? (
            <div className="flex items-center gap-3 text-foreground">
              <Check size={24} aria-hidden />
              <span className="text-[15px] leading-snug">Приложение уже установлено на это устройство</span>
            </div>
          ) : install.canPrompt || install.status !== 'idle' ? (
            <button
              type="button"
              disabled={install.status === 'opening' || install.status === 'accepted'}
              onClick={() => void promptInstall()}
              className="btn-accent flex items-center justify-center gap-3 rounded-full px-6 py-3.5 text-[16px] font-semibold disabled:opacity-80"
            >
              <DownloadSimple size={20} aria-hidden />
              {install.status === 'opening' ? 'Открываем установку…' : install.status === 'accepted' ? 'Приложение устанавливается' : 'Установить сейчас'}
            </button>
          ) : (
            <div className="flex items-center gap-3 text-foreground">
              <DeviceMobile size={24} aria-hidden />
              <span className="text-[16px] leading-snug">Несколько шагов в браузере — и готово</span>
            </div>
          )}
          {install.status === 'dismissed' ? (
            <p role="status" className="m-0 text-[15px] text-muted-foreground">
              Установка отменена. Можно воспользоваться инструкцией ниже.
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="m-0 text-[26px] font-bold tracking-tight text-foreground">Как установить</h2>
          <div className="panel flex gap-1 rounded-full p-1.5">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPlatform(tab.id)}
                className={cn(
                  'flex-1 rounded-full px-2 py-3 text-[15px] font-semibold transition-colors',
                  platform === tab.id ? 'bg-white/10 text-foreground' : 'text-muted-foreground',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <ol className="m-0 flex list-none flex-col p-0">
            {STEPS[platform].map((step, index) => {
              const StepIcon = step.icon;
              return (
                <li key={step.title} className="flex gap-4 border-b border-white/10 py-6 first:pt-2 last:border-b-0">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-[#141416] text-[16px] font-semibold text-foreground">
                    {index + 1}
                  </span>
                  <div className="flex min-w-0 flex-col gap-2">
                    <span className="flex items-center gap-3 text-[18px] font-semibold leading-snug text-foreground">
                      <StepIcon size={22} className="shrink-0" aria-hidden />
                      {step.title}
                    </span>
                    <span className="text-[16px] leading-relaxed text-muted-foreground">{step.text}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
      <TenantFooter />
    </div>
  );
}
